/**
 * [rtc-spike] Minimal WebRTC peer-connection lifecycle helper.
 *
 * The DEFAULT voice transport (`?rtc=0` is the escape hatch/rollback lever
 * back to the legacy path): instead of streaming PCM16 over the app
 * WebSocket (see useCallMode.ts), the browser opens an RTCPeerConnection
 * directly to OpenAI's Realtime API, with SDP exchanged through our own
 * backend (so the OpenAI API key never reaches the client). Framework-free
 * — no React — so it's trivially reusable/testable outside the hook.
 *
 * Everything else (transcripts, tool calls, the app WebSocket) is
 * untouched; this only replaces the audio transport.
 */

import { getAssistantApiUrl } from './assistant-config';
import { getDeviceToken } from './device-token';
// SDP-over-WebSocket negotiation (see that file's header for the full
// rationale) — negotiate() below tries this FIRST when a wsSend is
// available, falling back to the REST exchange further down.
import { negotiateOverWs, RtcWsNoAckError } from './rtc-ws-negotiate';
// RtcCallTimeoutError lives in its own dependency-free module — see that
// file's header for why (breaks a circular import with rtc-ws-negotiate.ts,
// which needs this same class for its own answer-timeout rejection).
// Re-exported below for back-compat: existing importers (useCallMode.ts,
// rtc-call.test.ts, rtc-ws-negotiate.test.ts) still pull it from here.
import { RtcCallTimeoutError } from './rtc-errors';
import { WSEventType } from '@/types/chat';

export { RtcCallTimeoutError };

/** Bounds the `/api/rtc/call` SDP-exchange fetch — see startRtcCall's use of
 *  `AbortSignal.timeout`. A dead network otherwise hangs the whole call
 *  start indefinitely with no feedback; RtcCallTimeoutError below gives the
 *  caller (useCallMode.ts's startCall catch) something distinguishable from
 *  a generic network/HTTP failure to surface a clearer message from. */
const RTC_CALL_TIMEOUT_MS = 15_000;

interface WirePeerConnectionOptions {
  onRemoteStream: (stream: MediaStream) => void;
  /** [rtc-reliability] Wired directly to `pc.onconnectionstatechange` —
   *  fires with `pc.connectionState` on every transition ('connected',
   *  'disconnected', 'failed', 'closed', ...). Optional so the (currently
   *  nonexistent) callers that don't care about connection health aren't
   *  forced to pass a no-op; useCallMode.ts's rtc-recovery driver is the one
   *  real consumer — see src/lib/rtc-recovery.ts's header for why nothing
   *  monitored this before. */
  onConnectionStateChange?: (state: RTCPeerConnectionState) => void;
}

/** Shared by startRtcCall/startRtcCallOffer — the two ways to build a peer
 *  connection's LISTENERS (track/connection-state) differ only in when the
 *  mic track itself gets attached, not in this wiring. */
function wirePeerConnection(
  pc: RTCPeerConnection,
  opts: WirePeerConnectionOptions
): void {
  pc.ontrack = (e) => {
    opts.onRemoteStream(e.streams[0] ?? new MediaStream([e.track]));
  };
  if (opts.onConnectionStateChange) {
    const onConnectionStateChange = opts.onConnectionStateChange;
    pc.onconnectionstatechange = () =>
      onConnectionStateChange(pc.connectionState);
  }
}

/**
 * Offer → answer — the SDP exchange shared by BOTH negotiation paths below
 * (startRtcCall/startRtcCallOffer), regardless of whether a real mic track
 * is on the peer connection yet (a track-less sendrecv audio transceiver
 * produces the same offer shape as one carrying a track — only the RTP
 * payload differs, so this exchange doesn't need to know or care which case
 * it's in).
 *
 * [SDP-over-WS] Two transports for the exchange itself, tried in order:
 *   1. Assistant WebSocket (`wsSend`, present ⇔ the socket is currently
 *      OPEN — the caller decides that, see useCallMode.ts) — always reaches
 *      the task that owns this session (see rtc-ws-negotiate.ts's header
 *      for why the REST POST below doesn't on a multi-task backend).
 *   2. REST `POST /api/rtc/call` — the original path, used when there's no
 *      open socket to negotiate over, OR the WS attempt got no ack (old
 *      backend that doesn't speak `rtc_call` at all).
 * An explicit WS-path failure (RtcWsNegotiateError / RtcCallTimeoutError)
 * does NOT fall back to REST — the backend already understood and acted on
 * the offer, so a REST retry would race a possibly still-completing attach.
 * Only the "no ack at all" case (RtcWsNoAckError) falls through.
 *
 * The REST leg is trickle-style: POSTs immediately after
 * setLocalDescription — official SDK behavior. OpenAI's realtime SDP
 * endpoint answers with public candidates already reachable without
 * waiting out full ICE gathering first; waiting was pure added latency on
 * every call start.
 */
async function negotiate(
  pc: RTCPeerConnection,
  sessionId: string,
  wsSend?: (json: string) => void
): Promise<{ callId: string }> {
  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);

  const localSdp = pc.localDescription?.sdp;
  if (!localSdp) {
    throw new Error('[rtc-spike] no local SDP after setLocalDescription');
  }

  if (wsSend) {
    try {
      const { sdp, callId } = await negotiateOverWs(wsSend, localSdp);
      await pc.setRemoteDescription({ type: 'answer', sdp });
      return { callId };
    } catch (err) {
      if (!(err instanceof RtcWsNoAckError)) {
        // Explicit rtc_call_failed or an answer-timeout — the backend
        // understood the offer and already tried it. Rethrow rather than
        // falling back to REST (see this function's header).
        throw err;
      }
      console.warn(
        '[rtc-ws] no rtc_call_ack — backend predates WebRTC-over-WS, falling back to REST /api/rtc/call'
      );
      // Fall through to the REST exchange below.
    }
  }

  let resp: Response;
  try {
    resp = await fetch(getAssistantApiUrl('/api/rtc/call'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Device-Token': getDeviceToken(),
      },
      body: JSON.stringify({
        session_id: sessionId,
        sdp: localSdp,
      }),
      signal: AbortSignal.timeout(RTC_CALL_TIMEOUT_MS),
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'TimeoutError') {
      throw new RtcCallTimeoutError();
    }
    throw err;
  }
  if (!resp.ok) {
    const detail = await resp.text().catch(() => '');
    throw new Error(
      `[rtc-spike] /api/rtc/call failed: ${resp.status} ${detail}`
    );
  }
  const { sdp, call_id: callId } = (await resp.json()) as {
    sdp: string;
    call_id: string;
  };

  await pc.setRemoteDescription({ type: 'answer', sdp });
  return { callId };
}

export interface StartRtcCallOptions extends WirePeerConnectionOptions {
  micTrack: MediaStreamTrack;
  micStream: MediaStream;
  sessionId: string;
  /** [SDP-over-WS] Present ⇔ the assistant WebSocket is currently OPEN —
   *  the caller decides that (see useCallMode.ts's runStartAttempt) and
   *  hands in a plain send function so this module stays framework-free
   *  and doesn't need to know about the socket/ref itself. Omitted →
   *  straight to the REST `/api/rtc/call` exchange, unchanged. */
  wsSend?: (json: string) => void;
}

export interface StartRtcCallResult {
  pc: RTCPeerConnection;
  callId: string;
}

/**
 * Opens an RTCPeerConnection, sends the mic track, exchanges SDP through
 * our backend's `/api/rtc/call` proxy, and wires the remote (agent) track
 * back to the caller via `onRemoteStream`. On any failure the
 * half-built peer connection is closed HERE before the throw — the caller
 * never receives a handle on the failure path, so it couldn't clean up.
 */
export async function startRtcCall(
  opts: StartRtcCallOptions
): Promise<StartRtcCallResult> {
  const pc = new RTCPeerConnection();
  try {
    wirePeerConnection(pc, opts);
    pc.addTrack(opts.micTrack, opts.micStream);
    const { callId } = await negotiate(pc, opts.sessionId, opts.wsSend);
    return { pc, callId };
  } catch (err) {
    pc.close();
    throw err;
  }
}

export interface StartRtcCallOfferOptions extends WirePeerConnectionOptions {
  sessionId: string;
  /** [SDP-over-WS] Same convention as StartRtcCallOptions.wsSend above. */
  wsSend?: (json: string) => void;
}

export interface StartRtcCallOfferResult {
  pc: RTCPeerConnection;
  callId: string;
  /** [FB34] Swaps the real mic track onto the pre-negotiated sendrecv
   *  transceiver once buildCapture resolves — the switchMic mechanism
   *  (rtc-call.ts's sibling in useCallMode.ts), proven on this pc already.
   *  No renegotiation needed: the backend forwards SDP opaquely, and a
   *  track-less sendrecv m-line is valid — the server's VAD just hears
   *  silence until the track lands. */
  attachTrack: (track: MediaStreamTrack) => Promise<void>;
}

/**
 * [FB34] Latency-overlap negotiation: builds the peer connection and
 * exchanges SDP WITHOUT a mic track up front (an empty sendrecv audio
 * transceiver instead), so this can run in PARALLEL with buildCapture's
 * getUserMedia rather than waiting for it to resolve first. Only safe when
 * the mic is already known-granted (see `canOverlapCaptureWithCall` below) —
 * the caller decides that; this function itself has no gate of its own.
 */
export async function startRtcCallOffer(
  opts: StartRtcCallOfferOptions
): Promise<StartRtcCallOfferResult> {
  const pc = new RTCPeerConnection();
  try {
    wirePeerConnection(pc, opts);
    const transceiver = pc.addTransceiver('audio', { direction: 'sendrecv' });
    const { callId } = await negotiate(pc, opts.sessionId, opts.wsSend);
    return {
      pc,
      callId,
      attachTrack: (track) => transceiver.sender.replaceTrack(track),
    };
  } catch (err) {
    pc.close();
    throw err;
  }
}

/**
 * [FB34] Decides whether the latency-overlap path above is safe to attempt:
 * only when the mic is ALREADY granted, never speculatively. Building a real
 * peer connection (and thereby swapping the live OpenAI call) behind a
 * permission prompt that may be abandoned would be worse than the sequential
 * path's latency — so an undetermined/unsupported Permissions API result
 * falls back to sequential, not "assume yes".
 *
 * `micGrantedThisSession` is the caller's own memory (set on the first
 * successful buildCapture — see useCallMode.ts), not just a fresh
 * `navigator.permissions.query` read: iOS Safari re-prompts for the mic on
 * EVERY page visit regardless of a prior grant, so a bare permissions query
 * would report 'prompt' again on that platform even mid-session — the
 * remembered flag is what gives a session's SECOND call onward the full
 * overlap there.
 */
export async function canOverlapCaptureWithCall(
  micGrantedThisSession: boolean
): Promise<boolean> {
  if (micGrantedThisSession) return true;
  const permissions =
    typeof navigator !== 'undefined' ? navigator.permissions : undefined;
  if (!permissions?.query) return false; // unsupported → sequential (safe).
  try {
    const status = await permissions.query({
      name: 'microphone' as PermissionName,
    });
    return status.state === 'granted';
  } catch {
    return false; // undetermined → sequential (safe).
  }
}

/**
 * Tears down an RTC call: closes the peer connection immediately (local,
 * synchronous, always succeeds), then best-effort notifies the backend so
 * it can release any server-side call state.
 *
 * [SDP-over-WS / delivery guarantee] When `wsSend` is provided (the
 * assistant socket is currently OPEN — same caller-decides convention as
 * negotiate()'s wsSend), the hangup goes out as an `rtc_hangup` WS frame
 * — same owning-task-affinity rationale as the call-start path (see
 * rtc-ws-negotiate.ts's header): the WS frame is routed by the connection
 * itself, so it always reaches the task that owns this session, unlike the
 * REST POST below, which is a brand-new HTTP request with no session
 * affinity and can land on a DIFFERENT task on a multi-task backend.
 *
 * The REST POST now fires UNCONDITIONALLY alongside the WS frame (it used
 * to be WS-XOR-REST, returning right after the WS send) — it's the delivery
 * backstop for a half-open socket whose `.send()` silently buffers instead
 * of throwing (a dead TCP connection the browser hasn't noticed yet look
 * exactly like a healthy one from `.send()`'s perspective). On a multi-task
 * backend this REST call may 404 (wrong task) — that's harmless, not an
 * error to react to, since the WS frame already reached the right one. And
 * if BOTH happen to land on the owning task, detaching an already-detached
 * call is idempotent server-side (`expected_call_id` no-ops the second one)
 * — so firing both is strictly safer than picking one, never double-harmful.
 *
 * No ack is awaited for either transport: both are fire-and-forget, and a
 * throwing/rejecting send (closed socket, network error) is swallowed —
 * the pc is already closed locally regardless.
 *
 * `callId` scopes the hangup to ONE OpenAI call: the backend detaches the
 * session's live call only when it still matches. Without it, hangup is
 * session-scoped and can tear down whichever call is CURRENTLY live — e.g.
 * a stale recall's cleanup killing a call the user legitimately restarted.
 * Optional purely for back-compat with callers that predate call ids.
 */
export async function stopRtcCall(
  pc: RTCPeerConnection,
  sessionId: string,
  callId?: string,
  wsSend?: (json: string) => void
): Promise<void> {
  pc.close();
  if (wsSend) {
    try {
      wsSend(
        JSON.stringify({
          type: WSEventType.RTC_HANGUP,
          ...(callId ? { call_id: callId } : {}),
        })
      );
    } catch {
      // Best-effort — the REST POST below is the backstop regardless.
    }
  }
  try {
    await fetch(getAssistantApiUrl('/api/rtc/hangup'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Device-Token': getDeviceToken(),
      },
      body: JSON.stringify({
        session_id: sessionId,
        ...(callId ? { call_id: callId } : {}),
      }),
    });
  } catch {
    // Best-effort — the pc is already closed locally regardless.
  }
}
